// src/pages/NotFound404.js
import React from "react";
import { Box, Button, Typography, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";

/**
 * 404 Cartoon Fishing Page
 * - Single-file component using MUI sx for styling + keyframe animations
 * - Inline SVG cartoon fishing illustration (lightweight, scalable)
 * - Responsive layout
 */

export default function NotFound404() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "linear-gradient(180deg, #dff3ff 0%, #f7fbff 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          maxWidth: 1100,
          width: "100%",
          borderRadius: 3,
          p: { xs: 3, md: 5 },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "540px 1fr" },
          gap: 4,
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        {/* Left: Cartoon Scene */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            "& svg": { width: "100%", height: "auto", display: "block" },
            // Keyframes defined in sx
            "@keyframes bob": {
              "0%": { transform: "translateY(0px)" },
              "50%": { transform: "translateY(-8px)" },
              "100%": { transform: "translateY(0px)" },
            },
            "@keyframes swim": {
              "0%": {
                transform: "translateX(-10px) translateY(0px) rotate(-5deg)",
              },
              "50%": {
                transform: "translateX(10px) translateY(-4px) rotate(5deg)",
              },
              "100%": {
                transform: "translateX(-10px) translateY(0px) rotate(-5deg)",
              },
            },
            "@keyframes floatLine": {
              "0%": { transform: "translateY(0px)" },
              "50%": { transform: "translateY(4px)" },
              "100%": { transform: "translateY(0px)" },
            },
          }}
        >
          {/* SVG Cartoon Fishing Scene */}
          <svg
            viewBox="0 0 600 400"
            preserveAspectRatio="xMidYMid meet"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="Cartoon fishing illustration"
          >
            {/* sky */}
            <defs>
              <linearGradient id="skyGrad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#BEE9FF" />
                <stop offset="1" stopColor="#E9F7FF" />
              </linearGradient>
              <linearGradient id="waterGrad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0" stopColor="#73c2ff" />
                <stop offset="1" stopColor="#47a3e6" />
              </linearGradient>
            </defs>

            <rect x="0" y="0" width="600" height="400" fill="url(#skyGrad)" />
            {/* sun */}
            <circle cx="520" cy="60" r="28" fill="#FFD166" opacity="0.95" />

            {/* water */}
            <g>
              <rect
                x="0"
                y="220"
                width="600"
                height="180"
                fill="url(#waterGrad)"
              />
              {/* subtle waves */}
              <g opacity="0.18" transform="translate(-20, 236)">
                <path
                  d="M0 20 C 40 0, 80 40, 120 20 C 160 0, 200 40, 240 20 C 280 0, 320 40, 360 20 C 400 0, 440 40, 480 20 C 520 0, 560 40, 600 20"
                  stroke="#ffffff"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                />
                <path
                  d="M0 40 C 40 20, 80 60, 120 40 C 160 20, 200 60, 240 40 C 280 20, 320 60, 360 40 C 400 20, 440 60, 480 40 C 520 20, 560 60, 600 40"
                  stroke="#ffffff"
                  strokeWidth="1.2"
                  fill="none"
                  strokeLinecap="round"
                />
              </g>
            </g>

            {/* boat group (bob animation) */}
            <g
              transform="translate(140,170)"
              style={{
                transformOrigin: "center",
                animation: "bob 3.6s ease-in-out infinite",
              }}
            >
              {/* boat body */}
              <ellipse cx="160" cy="120" rx="120" ry="28" fill="#6c4bdb" />
              <path d="M40 120 Q160 70 280 120" fill="#7e57c2" />
              {/* cabin / seat */}
              <rect
                x="130"
                y="80"
                rx="8"
                ry="8"
                width="70"
                height="36"
                fill="#FFF"
                opacity="0.95"
              />
              {/* person head */}
              <circle cx="160" cy="74" r="10" fill="#ffd1b6" />
              {/* torso */}
              <rect
                x="150"
                y="84"
                width="20"
                height="24"
                rx="6"
                fill="#2b2d42"
              />
              {/* fishing rod */}
              <g transform="translate(160,74)">
                <path
                  d="M0 5 C 40 -10, 120 -40, 160 -20"
                  stroke="#6b4b24"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="round"
                />
                {/* line */}
                <line
                  x1="160"
                  y1="-20"
                  x2="160"
                  y2="120"
                  stroke="#a0c4ff"
                  strokeWidth="2"
                  style={{ animation: "floatLine 2.6s ease-in-out infinite" }}
                  strokeLinecap="round"
                />
                {/* hook */}
                <path
                  d="M160 120 q6 8, -6 16"
                  stroke="#333"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                />
              </g>
            </g>

            {/* fish (swimming animation) */}
            <g
              transform="translate(360,260)"
              style={{ animation: "swim 2.8s ease-in-out infinite" }}
            >
              <g transform="scale(0.9)">
                <ellipse cx="0" cy="0" rx="26" ry="14" fill="#ff7b7b" />
                <path
                  d="M-26 0 q-8 -6, -18 0 q8 6, 18 0"
                  fill="#ff4d6d"
                  opacity="0.9"
                />
                <circle cx="-4" cy="-4" r="2.5" fill="#ffffff" />
                <path
                  d="M10 -6 q8 6, 14 0 q-6 6, -14 0"
                  fill="#ff6384"
                  opacity="0.6"
                />
              </g>
            </g>

            {/* extra small fishes */}
            <g>
              <g
                transform="translate(420,300)"
                style={{
                  animation: "swim 3.4s linear infinite",
                  transformOrigin: "center",
                }}
              >
                <ellipse cx="0" cy="0" rx="12" ry="6" fill="#ffd166" />
                <circle cx="2" cy="-2" r="1.2" fill="#fff" />
              </g>
              <g
                transform="translate(260,320)"
                style={{
                  animation: "swim 2.2s linear infinite",
                  transformOrigin: "center",
                }}
              >
                <ellipse cx="0" cy="0" rx="10" ry="5" fill="#8ce99a" />
              </g>
            </g>

            {/* a floating life-buoy */}
            <g transform="translate(520,290)">
              <circle
                cx="0"
                cy="0"
                r="22"
                fill="#fff"
                stroke="#ff6b6b"
                strokeWidth="6"
                opacity="0.95"
              />
            </g>
          </svg>
        </Box>

        {/* Right: Text and CTA */}
        <Box>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, color: "#0f172a", mb: 1 }}
          >
            404
          </Typography>
          <Typography variant="h6" sx={{ color: "text.secondary", mb: 2 }}>
            Oops — we couldn't find the page you're looking for.
          </Typography>

          <Typography sx={{ mb: 3, color: "#475569", lineHeight: 1.6 }}>
            Looks like our fisherman lost this page at sea. Try returning to the
            dashboard, or use the link below to go back home. If you keep seeing
            this, contact the admin.
          </Typography>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              onClick={() => navigate("/")}
              sx={{
                background: "linear-gradient(90deg,#6a11cb 0%,#2575fc 100%)",
                color: "white",
                px: 4,
                py: 1.25,
                borderRadius: 2,
                boxShadow: "0 8px 20px rgba(38, 120, 250, 0.18)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 30px rgba(38, 120, 250, 0.22)",
                },
              }}
            >
              Go Home
            </Button>

            <Button
              variant="outlined"
              onClick={() => navigate("/Pdf-View")}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                color: "#0f172a",
                borderColor: "#d1d5db",
                "&:hover": { background: "#f1f5f9" },
              }}
            >
              My Papers
            </Button>
          </Box>

          <Box sx={{ mt: 4, color: "#94a3b8", fontSize: 13 }}>
            <div>Tip: If you typed a URL, double-check it for errors.</div>
            <div>Need help? Contact support or check the dashboard.</div>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
